/**
 * Moving one straight wall line of one room (PRP-SKETCH-FIELD-3 J1-J4, J17).
 *
 * The line is the room's own corner group on the wall's normal axis, tied by
 * straight walls only (never by measurements), so a move never reaches a
 * neighbour (J2). Measurements that cross the line change with it; how they
 * change is the caller's rule (J3 records it, J4 reverses it, J17 refuses it).
 * Openings on the walls that run into the line keep their world positions.
 */
import { IN_PER_FT } from './policy';
import { EPS, axisOf, clamp, edgeOf, round6, snapIn, unit } from './geom';
import { buildUF, compOf } from './uf';
import type { Axis, Measurement, Room, SharedMap } from './types';

/** The axis a wall's line moves along (its normal): y for a horizontal wall. */
export function normalAxis(r: Room, vid: number): Axis | null {
  const E = edgeOf(r, vid);
  const t = E ? axisOf(E.a, E.b) : null;
  return t === 'h' ? 'y' : t === 'v' ? 'x' : null;
}

/** The room's corners on wall `vid`'s line (straight walls only, no measurements). */
export function lineGroup(r: Room, vid: number, ax: Axis): Set<number> {
  return new Set(compOf(r, buildUF(r, false), ax, vid).map((c) => c.id));
}

/** Measurements of `r` that cross the line `ids` on axis `ax` (one corner on it). */
export function crossing(r: Room, ids: Set<number>, ax: Axis): Measurement[] {
  return r.measurements.filter((m) => m.axis === ax && ids.has(m.a) !== ids.has(m.b));
}

/** Rooms (other than `except`) sharing another wall on the same line: their walls are held (J2). */
export function heldBy(r: Room, ids: Set<number>, vid: number, M: SharedMap, except: Set<string>): string[] {
  const out = new Set<string>();
  M.get(r.id)?.forEach((ivs, v) => {
    if (v === vid || !ids.has(v)) return;
    const E = edgeOf(r, v);
    if (!E || !ids.has(E.b.id)) return;
    ivs.forEach((iv) => { if (!except.has(iv.orid)) out.add(iv.orid); });
  });
  return [...out];
}

export interface LineMove {
  /** Signed move on the axis, feet (new minus old). */
  d: number;
  /** Each crossing measurement with its distance before and after, feet. */
  measured: { m: Measurement; before: number; after: number }[];
}

/**
 * Put every corner of the line at coordinate `to` on axis `ax`. Openings on the
 * walls running into the line keep their world positions (their offset moves by
 * the start corner's move, clamped to the wall).
 */
export function moveLine(r: Room, ids: Set<number>, ax: Axis, to: number): LineMove {
  const first = r.corners.find((c) => ids.has(c.id))!;
  const d = to - first[ax];
  const at = (id: number) => r.corners.find((c) => c.id === id);
  const dist = (m: Measurement) => Math.abs(at(m.b)![ax] - at(m.a)![ax]);
  const measured = crossing(r, ids, ax).filter((m) => at(m.a) && at(m.b)).map((m) => ({ m, before: dist(m), after: 0 }));
  // Openings on a wall that starts on the line and leaves it.
  const shifted = r.openings.filter((o) => {
    const E = edgeOf(r, o.edgeStart);
    return !!E && ids.has(E.a.id) && !ids.has(E.b.id);
  });
  for (const c of r.corners) if (ids.has(c.id)) c[ax] = to;
  for (const o of shifted) {
    const E = edgeOf(r, o.edgeStart)!;
    const u = unit(E.a, E.b);
    const L = Math.hypot(E.b.x - E.a.x, E.b.y - E.a.y);
    const off = clamp(o.offFt - d * u[ax], 0, Math.max(0, L - o.widthFt));
    o.offFt = onInch(off);
  }
  measured.forEach((x) => { x.after = dist(x.m); });
  return { d, measured };
}

/** An offset back on the whole inch it came from (float noise from moving there and back). */
function onInch(v: number): number {
  const s = snapIn(v);
  return Math.abs(s - v) < EPS ? s : v;
}

/**
 * J3/J4: a measurement crossing a moved wall changes by `deltaIn` (signed inches, on
 * the core's grid). `adjustIn` is the running total and `baseFt` the reading it started
 * from, so `valueFt` is always `baseFt + adjustIn / 12`, computed the same way every
 * time: a restore, in any order, returns the very same reading (DD-SF3.11 item 4), and
 * the record goes once nothing is left of it.
 */
export function recordAdjust(m: Measurement, deltaIn: number): void {
  const recorded = m.adjustIn != null && m.baseFt != null && m.valueFt === m.baseFt + m.adjustIn / IN_PER_FT;
  // A reading changed since (or recorded by an older build) starts again from what is there.
  const base = recorded ? m.baseFt! : m.valueFt - (m.adjustIn ?? 0) / IN_PER_FT;
  const adj = round6((m.adjustIn ?? 0) + deltaIn);
  if (adj === 0) {
    m.valueFt = base;
    delete m.adjustIn;
    delete m.baseFt;
    return;
  }
  m.baseFt = base;
  m.adjustIn = adj;
  m.valueFt = base + adj / IN_PER_FT;
}
